import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Thin wrapper around the Web Speech API (SpeechRecognition).
 * Browser support: Chrome, Edge, Safari (webkit prefix). Firefox has no support.
 *
 * Returns:
 *   - supported:     boolean
 *   - listening:     boolean (true between `start()` and onend)
 *   - transcript:    the committed (final) transcript so far for this session
 *   - interim:       the current in-flight (partial) transcript
 *   - error:         string | null — last error code from the browser
 *   - start(), stop()
 *   - reset():       clears transcript/interim/error (useful between turns)
 */
function getSpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function useSpeechRecognition({ lang = "es-ES" } = {}) {
  const SR = getSpeechRecognitionCtor();
  const supported = !!SR;

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const finalRef = useRef("");

  const start = useCallback(() => {
    if (!SR) {
      setError("unsupported");
      return;
    }
    setError(null);
    finalRef.current = "";
    setTranscript("");
    setInterim("");

    const rec = new SR();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart = () => setListening(true);
    rec.onresult = (event) => {
      let interimBuf = "";
      let finalBuf = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalBuf += r[0].transcript;
        else interimBuf += r[0].transcript;
      }
      if (finalBuf) {
        finalRef.current = (finalRef.current + " " + finalBuf).trim();
        setTranscript(finalRef.current);
      }
      setInterim(interimBuf);
    };
    rec.onerror = (e) => {
      setError(e?.error || "unknown");
    };
    rec.onend = () => {
      setListening(false);
      setInterim("");
    };

    try {
      rec.start();
      recognitionRef.current = rec;
    } catch (e) {
      setError(String(e?.message || e));
      setListening(false);
    }
  }, [SR, lang]);

  const stop = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const reset = useCallback(() => {
    finalRef.current = "";
    setTranscript("");
    setInterim("");
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      const rec = recognitionRef.current;
      if (rec) {
        try {
          rec.abort();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  return { supported, listening, transcript, interim, error, start, stop, reset };
}
